import { Text, List, ListItem, ListIcon, Button, Menu, MenuButton, MenuList, MenuItem } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { useIntl, FormattedMessage } from 'react-intl';

import CustomNavLink from '../utils/CustomNavLink';

import { ReactComponent as DashboardIcon } from '../../icons/dashboard.svg';
import { ReactComponent as PortfolioIcon } from '../../icons/portfolio.svg';
import { ReactComponent as TurbineIcon } from '../../icons/turbine.svg';

import { ReactComponent as ArrowDropDownIcon } from '../../icons/arrow_drop_down.svg';

const MainNavigation = () => {
    const intl = useIntl();

    return (
        <Wrapper>
            <StyledList>
                <ListItem>
                    <CustomNavLink exact to="/">
                        <span>
                            <ListIcon as={DashboardIcon} />
                        </span>

                        <LinkText>
                            <FormattedMessage id="common_dashboard" />
                        </LinkText>
                    </CustomNavLink>
                </ListItem>

                <ListItem>
                    <CustomNavLink exact to="/portfolios">
                        <span>
                            <ListIcon as={PortfolioIcon} />
                        </span>

                        <LinkText>
                            <FormattedMessage id="portfolios_title" />
                        </LinkText>
                    </CustomNavLink>
                </ListItem>

                <MenuWrapper>
                    <Menu>
                        <StyledMenuButton
                            as={Button}
                            aria-label={intl.formatMessage({ id: 'common_items' })}
                            rightIcon={<ArrowDropDownIcon color="var(--chakra-colors-gray-600)" />}
                            leftIcon={<TurbineIcon color="currentColor" />}
                            variant="ghost"
                            iconSpacing={1}
                        >
                            <Text
                                as="span"
                                textTransform="capitalize"
                                color="var(--chakra-colors-gray-600)"
                                _hover={{
                                    color: 'var(--chakra-colors-black)',
                                }}
                            >
                                <FormattedMessage id="common_items" />
                            </Text>
                        </StyledMenuButton>

                        <StyledMenuList>
                            <MenuItem as={CustomNavLink} to="/loads">
                                <FormattedMessage id="load_title" />
                            </MenuItem>

                            <MenuItem as={CustomNavLink} to="/batteries">
                                <FormattedMessage id="batteries_title" />
                            </MenuItem>

                            <MenuItem as={CustomNavLink} to="/wind">
                                <FormattedMessage id="wind_title" />
                            </MenuItem>

                            {process.env.REACT_APP_SHOW_EXPERIMENTAL_FEATURES === 'true' && (
                                <MenuItem as={CustomNavLink} to="/solar">
                                    <FormattedMessage id="solar_title" />
                                </MenuItem>
                            )}

                            <MenuItem as={CustomNavLink} to="/hedges-contracts">
                                <FormattedMessage id="hedges_and_contracts_title" />
                            </MenuItem>
                        </StyledMenuList>
                    </Menu>
                </MenuWrapper>
            </StyledList>

            <Button as={CustomNavLink} to="/run-a-study" mr="30px">
                <FormattedMessage id="dashboard_run_a_study_btn" />
            </Button>
        </Wrapper>
    );
};

const Wrapper = styled.nav`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    ul {
        li {
            a {
                outline: 0;
                padding-top: 10px;
                padding-bottom: 10px;
                &:focus {
                    box-shadow: var(--chakra-shadows-outline);
                }
            }
        }

        div {
            button {
                span:nth-of-type(2) span {
                    &.active {
                        color: var(--chakra-colors-black) !important;
                    }
                }
            }
        }
    }

    @media screen and (max-width: 899px) {
        display: inline-block;
        width: auto;
        a:last-of-type {
            display: none;
        }
        ul {
            display: none;
        }
    }
`;

const LinkText = styled.span`
    line-height: 1.25;
`;

const listItemActiveStyles = `
    color: var(--chakra-colors-black);
    svg {
        opacity: 1;
        color: var(--chakra-colors-blue-500);
    }
`;

const StyledList = styled(List)`
    display: flex;

    > li {
        &:not(:last-child) {
            margin-right: 30px;
        }
    }

    li {
        display: flex;
        position: relative;

        a {
            display: flex;
            align-items: center;
            font-weight: 500;
            color: var(--chakra-colors-gray-600);
            border-radius: var(--chakra-radii-base);
            text-transform: capitalize;

            svg {
                font-size: 1.5em; // adjust icon size based on font-size
                opacity: 0.5;
            }

            &.active {
                ${listItemActiveStyles};
            }
        }

        ul {
            display: none;
            flex-direction: column;
            background: #fff;
            border-radius: var(--chakra-radii-base);
            box-shadow: var(--chakra-shadows-lg);
            position: absolute;
            top: 100%;
            left: 0;
            min-width: 280px;
            padding: 8px;

            li {
                cursor: pointer;

                &:hover {
                    background: rgba(41, 110, 201, 0.07);
                }
            }

            a {
                line-height: 1;
                border-radius: var(--chakra-radii-base);
                width: 100%;

                span {
                    display: inline;
                }
            }

            a:focus {
                color: var(--chakra-colors-blue-500);
                background: rgba(41, 110, 201, 0.07);
            }

            a.active {
                color: var(--chakra-colors-blue-500);
                pointer-events: none;
            }
        }

        &:hover > a,
        &:focus > a {
            ${listItemActiveStyles};
        }

        &:hover ul,
        &:focus-within ul {
            display: flex;

            svg {
                opacity: 1;
            }
        }
    }
`;

const MenuWrapper = styled.div`
    display: flex;
    align-items: center;
    svg {
        opacity: 0.5;
    }

    &:hover {
        color: var(--chakra-colors-black);
        span:first-of-type svg {
            opacity: 1;
        }
    }
`;

const StyledMenuList = styled(MenuList)`
    > * {
        font-weight: 500;
        color: var(--chakra-colors-gray-600) !important;
        padding: 10px 30px 10px 25px !important;

        &[aria-current='page'] {
            color: var(--chakra-colors-black) !important;
        }
    }

    > *:first-of-type {
        margin-top: 8px;
    }

    > *:last-of-type {
        margin-bottom: 10px;
    }
`;

const StyledMenuButton = styled(MenuButton)`
    padding: 0;

    &:hover {
        > span svg path {
            color: var(--chakra-colors-blue-500);
        }

        span:nth-of-type(1) {
            color: var(--chakra-colors-black);
        }
    }

    &[aria-expanded='true'] {
        p {
            color: var(--chakra-colors-black);
        }
        svg path {
            fill: var(--chakra-colors-blue-500);
        }
    }
`;

export default MainNavigation;
